import React, { useState, useRef, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import styles from './FontsPanel.module.css';
import { useGoogleFonts } from '../../../hooks/useGoogleFonts';
import { useFormStep } from '../../../context/FormStepContext';

const FontsPanel = ({ isOpen, onClose, isLoading, showBothSidebars = false }) => {
  const { fonts, loading: fontsLoading } = useGoogleFonts();
  const { selectedFonts, setSelectedFonts } = useFormStep();
  const [applySameFont, setApplySameFont] = useState(false);
  
  // Use fonts from context, with local state as fallback
  const [headerFont, setHeaderFont] = useState(selectedFonts.headerFont || { family: '', weight: '' });
  const [calloutTextFont, setCalloutTextFont] = useState(selectedFonts.calloutTextFont || { family: '', weight: '' });
  const [bodyFont, setBodyFont] = useState(selectedFonts.bodyFont || { family: '', weight: '' });
  
  // Search states for each field
  const [headerSearch, setHeaderSearch] = useState('');
  const [calloutTextSearch, setCalloutTextSearch] = useState('');
  const [bodySearch, setBodySearch] = useState('');
  
  // Show dropdown states
  const [showHeaderDropdown, setShowHeaderDropdown] = useState(false);
  const [showCalloutTextDropdown, setShowCalloutTextDropdown] = useState(false);
  const [showBodyDropdown, setShowBodyDropdown] = useState(false);
  const [showHeaderWeights, setShowHeaderWeights] = useState(false);
  const [showCalloutTextWeights, setShowCalloutTextWeights] = useState(false);
  const [showBodyWeights, setShowBodyWeights] = useState(false);
  
  // Refs for click outside detection
  const headerRef = useRef(null);
  const calloutTextRef = useRef(null);
  const bodyRef = useRef(null);

  // Filter fonts based on search query
  const filterFonts = (query, allFonts) => {
    if (!query.trim()) return allFonts.slice(0, 20); // Show first 20 if no query
    const lowerQuery = query.toLowerCase();
    return allFonts
      .filter(font => font.family.toLowerCase().includes(lowerQuery))
      .slice(0, 20); // Limit to 20 results
  };

  // Handle font selection
  const handleFontSelect = (font, fieldType) => {
    const fontData = { family: font.family, weight: font.weights[0] || '400' };
    
    if (fieldType === 'header') {
      setHeaderFont(fontData);
      setHeaderSearch(font.family);
      setShowHeaderDropdown(false);
      setShowHeaderWeights(true); // Show weights when font is selected
      
      // Auto-fill callout text and body with header font if they don't have a font selected
      if (!applySameFont) {
        if (!calloutTextFont.family) {
          const calloutTextData = { family: font.family, weight: '400' }; // Default to 400 for callout text
          setCalloutTextFont(calloutTextData);
          setCalloutTextSearch(font.family);
        }
        if (!bodyFont.family) {
          const bodyData = { family: font.family, weight: '400' }; // Default to 400 for body
          setBodyFont(bodyData);
          setBodySearch(font.family);
        }
        setSelectedFonts(prev => ({
          ...prev,
          headerFont: fontData,
          calloutTextFont: calloutTextFont.family ? prev.calloutTextFont : { family: font.family, weight: '400' },
          bodyFont: bodyFont.family ? prev.bodyFont : { family: font.family, weight: '400' }
        }));
      } else {
        // If applySameFont is checked, sync all three
        setCalloutTextFont(fontData);
        setBodyFont(fontData);
        setCalloutTextSearch(font.family);
        setBodySearch(font.family);
        setShowCalloutTextWeights(true);
        setShowBodyWeights(true);
        const updatedFonts = { headerFont: fontData, calloutTextFont: fontData, bodyFont: fontData };
        setSelectedFonts(updatedFonts);
      }
    } else if (fieldType === 'calloutText') {
      setCalloutTextFont(fontData);
      setCalloutTextSearch(font.family);
      setShowCalloutTextDropdown(false);
      setShowCalloutTextWeights(true); // Show weights when font is selected
      if (applySameFont) {
        setHeaderFont(fontData);
        setBodyFont(fontData);
        setHeaderSearch(font.family);
        setBodySearch(font.family);
        setShowHeaderWeights(true);
        setShowBodyWeights(true);
        const updatedFonts = { headerFont: fontData, calloutTextFont: fontData, bodyFont: fontData };
        setSelectedFonts(updatedFonts);
      } else {
        setSelectedFonts(prev => ({ ...prev, calloutTextFont: fontData }));
      }
    } else if (fieldType === 'body') {
      setBodyFont(fontData);
      setBodySearch(font.family);
      setShowBodyDropdown(false);
      setShowBodyWeights(true); // Show weights when font is selected
      if (applySameFont) {
        setHeaderFont(fontData);
        setCalloutTextFont(fontData);
        setHeaderSearch(font.family);
        setCalloutTextSearch(font.family);
        setShowHeaderWeights(true);
        setShowCalloutTextWeights(true);
        const updatedFonts = { headerFont: fontData, calloutTextFont: fontData, bodyFont: fontData };
        setSelectedFonts(updatedFonts);
      } else {
        setSelectedFonts(prev => ({ ...prev, bodyFont: fontData }));
      }
    }
  };

  // Handle weight change
  const handleWeightChange = (weight, fieldType) => {
    if (fieldType === 'header') {
      const updated = { ...headerFont, weight };
      setHeaderFont(updated);
      setShowHeaderWeights(false); // Close weights list when weight is selected
      if (applySameFont) {
        setCalloutTextFont(updated);
        setBodyFont(updated);
        setShowCalloutTextWeights(false);
        setShowBodyWeights(false);
        setSelectedFonts({ headerFont: updated, calloutTextFont: updated, bodyFont: updated });
      } else {
        setSelectedFonts(prev => ({ ...prev, headerFont: updated }));
      }
    } else if (fieldType === 'calloutText') {
      const updated = { ...calloutTextFont, weight };
      setCalloutTextFont(updated);
      setShowCalloutTextWeights(false); // Close weights list when weight is selected
      if (applySameFont) {
        setHeaderFont(updated);
        setBodyFont(updated);
        setShowHeaderWeights(false);
        setShowBodyWeights(false);
        setSelectedFonts({ headerFont: updated, calloutTextFont: updated, bodyFont: updated });
      } else {
        setSelectedFonts(prev => ({ ...prev, calloutTextFont: updated }));
      }
    } else if (fieldType === 'body') {
      const updated = { ...bodyFont, weight };
      setBodyFont(updated);
      setShowBodyWeights(false); // Close weights list when weight is selected
      if (applySameFont) {
        setHeaderFont(updated);
        setCalloutTextFont(updated);
        setShowHeaderWeights(false);
        setShowCalloutTextWeights(false);
        setSelectedFonts({ headerFont: updated, calloutTextFont: updated, bodyFont: updated });
      } else {
        setSelectedFonts(prev => ({ ...prev, bodyFont: updated }));
      }
    }
  };

  // Map numeric weight to weight name
  const getWeightName = (weight) => {
    const weightMap = {
      '100': 'Thin',
      '200': 'Extra Light',
      '300': 'Light',
      '400': 'Regular',
      '500': 'Medium',
      '600': 'Semi Bold',
      '700': 'Bold',
      '800': 'Extra Bold',
      '900': 'Black',
    };
    return weightMap[weight] || weight;
  };

  // Format weight display: "Font Name Bold 700"
  const formatWeightDisplay = (fontFamily, weight) => {
    const weightName = getWeightName(weight);
    return `${fontFamily} ${weightName} ${weight}`;
  };

  // Format display for search input: "Font Name Bold" (without number)
  const formatInputDisplay = (fontFamily, weight) => {
    if (!fontFamily) return '';
    const weightName = getWeightName(weight);
    return `${fontFamily} ${weightName}`;
  };

  // Handle clearing font selection
  const handleClearFont = (fieldType) => {
    if (fieldType === 'header') {
      setHeaderFont({ family: '', weight: '' });
      setHeaderSearch('');
      setSelectedFonts(prev => ({ ...prev, headerFont: { family: '', weight: '' } }));
    } else if (fieldType === 'calloutText') {
      setCalloutTextFont({ family: '', weight: '' });
      setCalloutTextSearch('');
      setSelectedFonts(prev => ({ ...prev, calloutTextFont: { family: '', weight: '' } }));
    } else if (fieldType === 'body') {
      setBodyFont({ family: '', weight: '' });
      setBodySearch('');
      setSelectedFonts(prev => ({ ...prev, bodyFont: { family: '', weight: '' } }));
    }
  };

  // Get available weights for selected font
  const getAvailableWeights = (fontFamily) => {
    const font = fonts.find(f => f.family === fontFamily);
    return font ? font.weights : ['400'];
  };

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setShowHeaderDropdown(false);
      }
      if (calloutTextRef.current && !calloutTextRef.current.contains(event.target)) {
        setShowCalloutTextDropdown(false);
      }
      if (bodyRef.current && !bodyRef.current.contains(event.target)) {
        setShowBodyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync local state with context when context changes
  useEffect(() => {
    if (selectedFonts.headerFont?.family && selectedFonts.headerFont.family !== headerFont.family) {
      setHeaderFont(selectedFonts.headerFont);
      setHeaderSearch(selectedFonts.headerFont.family);
    }
    if (selectedFonts.calloutTextFont?.family && selectedFonts.calloutTextFont.family !== calloutTextFont.family) {
      setCalloutTextFont(selectedFonts.calloutTextFont);
      setCalloutTextSearch(selectedFonts.calloutTextFont.family);
    }
    if (selectedFonts.bodyFont?.family && selectedFonts.bodyFont.family !== bodyFont.family) {
      setBodyFont(selectedFonts.bodyFont);
      setBodySearch(selectedFonts.bodyFont.family);
    }
  }, [selectedFonts]);

  // Sync search values with selected fonts
  useEffect(() => {
    if (headerFont.family && !headerSearch) {
      setHeaderSearch(headerFont.family);
    }
    if (calloutTextFont.family && !calloutTextSearch) {
      setCalloutTextSearch(calloutTextFont.family);
    }
    if (bodyFont.family && !bodySearch) {
      setBodySearch(bodyFont.family);
    }
  }, [headerFont.family, calloutTextFont.family, bodyFont.family]);

  if (!isOpen) return null;

  return (
    <div className={styles.fontsPanelOverlay}>
      <div className={`${styles.fontsPanel} ${showBothSidebars ? styles.fontsPanelShifted : ''}`}>
        <div className={styles.fontsPanelHeader}>
          <h2 className={styles.fontsPanelTitle}>Fonts</h2>
          <button
            className={styles.fontsPanelClose}
            onClick={onClose}
            aria-label="Close fonts panel"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.fontsPanelContent}>

              {/* Apply Same Font Checkbox */}
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={applySameFont}
                  onChange={(e) => {
                    setApplySameFont(e.target.checked);
                    if (e.target.checked && headerFont.family) {
                      setCalloutTextFont(headerFont);
                      setBodyFont(headerFont);
                      setCalloutTextSearch(headerFont.family);
                      setBodySearch(headerFont.family);
                      setSelectedFonts({ headerFont, calloutTextFont: headerFont, bodyFont: headerFont });
                    }
                  }}
                  className={styles.checkboxInput}
                />
                <span className={styles.checkboxText}>Apply same for Header, callout text and body</span>
              </label>

              {/* Header Font Section */}
              <div className={styles.fontSection} ref={headerRef}>
                <h3 className={styles.fontSectionTitle}>Header Font</h3>
                <div className={styles.searchInputContainer}>
                  {!headerFont.family && (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.searchIcon}>
                      <path d="M10.421 0C14.5954 0 18 3.40462 18 7.57899C18 11.7534 14.5954 15.158 10.421 15.158C8.67422 15.158 7.06155 14.5613 5.77761 13.5621L1.61719 17.7224C1.24712 18.0925 0.647591 18.0925 0.277544 17.7224C-0.0925028 17.3524 -0.0925265 16.7519 0.277544 16.3828L4.43796 12.2234C3.43876 10.9383 2.84203 9.32678 2.84203 7.58006C2.84203 3.40569 6.24668 0.00106589 10.4211 0.00106589L10.421 0ZM10.421 1.89475C7.27068 1.89475 4.73667 4.42874 4.73667 7.57899C4.73667 10.7292 7.27068 13.2632 10.421 13.2632C13.5712 13.2632 16.1052 10.7292 16.1052 7.57899C16.1052 4.42874 13.5712 1.89475 10.421 1.89475Z" fill="#D2D2D2"/>
                    </svg>
                  )}
                  <input
                    type="text"
                    placeholder="Search fonts"
                    value={headerFont.family ? formatInputDisplay(headerFont.family, headerFont.weight) : headerSearch}
                    onChange={(e) => {
                      if (!headerFont.family) {
                        setHeaderSearch(e.target.value);
                        setShowHeaderDropdown(true);
                      }
                    }}
                    onFocus={() => {
                      if (!headerFont.family) {
                        setShowHeaderDropdown(true);
                      }
                    }}
                    className={`${styles.searchInput} ${headerFont.family ? styles.searchInputSelected : ''}`}
                    style={{
                      fontFamily: headerFont.family ? `"${headerFont.family}", sans-serif` : 'inherit',
                      fontWeight: headerFont.family ? headerFont.weight : 'inherit'
                    }}
                    readOnly={!!headerFont.family}
                  />
                  {headerFont.family && (
                    <button
                      className={styles.clearButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearFont('header');
                      }}
                      aria-label="Clear font selection"
                    >
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.5" y="0.5" width="21" height="21" rx="10.5" fill="#D0EAFE" fillOpacity="0.2"/>
                    <rect x="0.5" y="0.5" width="21" height="21" rx="10.5" stroke="#78B3F3"/>
                    <path d="M14.5 8.1125L13.8875 7.5L11 10.3875L8.1125 7.5L7.5 8.1125L10.3875 11L7.5 13.8875L8.1125 14.5L11 11.6125L13.8875 14.5L14.5 13.8875L11.6125 11L14.5 8.1125Z" fill="#505050"/>
                    </svg>
                    </button>
                  )}
                  {showHeaderDropdown && !headerFont.family && !fontsLoading && fonts.length > 0 && (
                    <div className={styles.dropdown}>
                      {filterFonts(headerSearch, fonts).map((font) => (
                        <div
                          key={font.family}
                          className={styles.dropdownItem}
                          onClick={() => handleFontSelect(font, 'header')}
                        >
                          {font.family}
                        </div>
                      ))}
                      {filterFonts(headerSearch, fonts).length === 0 && headerSearch.trim() && (
                        <div className={styles.dropdownItem}>No fonts found</div>
                      )}
                    </div>
                  )}
                </div>
                {headerFont.family && (
                  <div className={styles.fontWeightList}>
                    <div 
                      className={styles.fontNameRow}
                      onClick={() => setShowHeaderWeights(!showHeaderWeights)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className={styles.chevronIcon} style={{ transform: showHeaderWeights ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>›</span>
                      <span className={styles.fontNameText}>{headerFont.family}</span>
                    </div>
                    {showHeaderWeights && (
                      <div className={styles.weightList}>
                        {getAvailableWeights(headerFont.family).map(weight => (
                          <div
                            key={weight}
                            className={`${styles.weightItem} ${headerFont.weight === weight ? styles.weightItemSelected : ''}`}
                            onClick={() => handleWeightChange(weight, 'header')}
                            style={{
                              fontWeight: weight
                            }}
                          >
                            {formatWeightDisplay(headerFont.family, weight)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Callout Text Font Section */}
              <div className={styles.fontSection} ref={calloutTextRef}>
                <h3 className={styles.fontSectionTitle}>Callout Text Font</h3>
                <div className={styles.searchInputContainer}>
                  {!calloutTextFont.family && (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.searchIcon}>
                    <path d="M10.421 0C14.5954 0 18 3.40462 18 7.57899C18 11.7534 14.5954 15.158 10.421 15.158C8.67422 15.158 7.06155 14.5613 5.77761 13.5621L1.61719 17.7224C1.24712 18.0925 0.647591 18.0925 0.277544 17.7224C-0.0925028 17.3524 -0.0925265 16.7519 0.277544 16.3828L4.43796 12.2234C3.43876 10.9383 2.84203 9.32678 2.84203 7.58006C2.84203 3.40569 6.24668 0.00106589 10.4211 0.00106589L10.421 0ZM10.421 1.89475C7.27068 1.89475 4.73667 4.42874 4.73667 7.57899C4.73667 10.7292 7.27068 13.2632 10.421 13.2632C13.5712 13.2632 16.1052 10.7292 16.1052 7.57899C16.1052 4.42874 13.5712 1.89475 10.421 1.89475Z" fill="#D2D2D2"/>
                  </svg>
                  )}
                  <input
                    type="text"
                    placeholder="Search fonts"
                    value={calloutTextFont.family ? formatInputDisplay(calloutTextFont.family, calloutTextFont.weight) : calloutTextSearch}
                    onChange={(e) => {
                      if (!calloutTextFont.family && !applySameFont) {
                        setCalloutTextSearch(e.target.value);
                        setShowCalloutTextDropdown(true);
                      }
                    }}
                    onFocus={() => {
                      if (!calloutTextFont.family && !applySameFont) {
                        setShowCalloutTextDropdown(true);
                      }
                    }}
                    className={`${styles.searchInput} ${calloutTextFont.family ? styles.searchInputSelected : ''}`}
                    style={{
                      fontFamily: calloutTextFont.family ? `"${calloutTextFont.family}", sans-serif` : 'inherit',
                      fontWeight: calloutTextFont.family ? calloutTextFont.weight : 'inherit'
                    }}
                    readOnly={!!calloutTextFont.family || applySameFont}
                    disabled={applySameFont && !calloutTextFont.family}
                  />
                  {calloutTextFont.family && !applySameFont && (
                    <button
                      className={styles.clearButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearFont('calloutText');
                      }}
                      aria-label="Clear font selection"
                    >
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.5" y="0.5" width="21" height="21" rx="10.5" fill="#D0EAFE" fillOpacity="0.2"/>
                    <rect x="0.5" y="0.5" width="21" height="21" rx="10.5" stroke="#78B3F3"/>
                    <path d="M14.5 8.1125L13.8875 7.5L11 10.3875L8.1125 7.5L7.5 8.1125L10.3875 11L7.5 13.8875L8.1125 14.5L11 11.6125L13.8875 14.5L14.5 13.8875L11.6125 11L14.5 8.1125Z" fill="#505050"/>
                    </svg>
                    </button>
                  )}
                  {showCalloutTextDropdown && !calloutTextFont.family && !applySameFont && !fontsLoading && fonts.length > 0 && (
                    <div className={styles.dropdown}>
                      {filterFonts(calloutTextSearch, fonts).map((font) => (
                        <div
                          key={font.family}
                          className={styles.dropdownItem}
                          onClick={() => handleFontSelect(font, 'calloutText')}
                        >
                          {font.family}
                        </div>
                      ))}
                      {filterFonts(calloutTextSearch, fonts).length === 0 && calloutTextSearch.trim() && (
                        <div className={styles.dropdownItem}>No fonts found</div>
                      )}
                    </div>
                  )}
                </div>
                {calloutTextFont.family && (
                  <div className={styles.fontWeightList}>
                    <div 
                      className={styles.fontNameRow}
                      onClick={() => !applySameFont && setShowCalloutTextWeights(!showCalloutTextWeights)}
                      style={{ cursor: applySameFont ? 'default' : 'pointer' }}
                    >
                      <span className={styles.chevronIcon} style={{ transform: showCalloutTextWeights ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', opacity: applySameFont ? 0.5 : 1 }}>›</span>
                      <span className={styles.fontNameText}>{calloutTextFont.family}</span>
                    </div>
                    {showCalloutTextWeights && !applySameFont && (
                      <div className={styles.weightList}>
                        {getAvailableWeights(calloutTextFont.family).map(weight => (
                          <div
                            key={weight}
                            className={`${styles.weightItem} ${calloutTextFont.weight === weight ? styles.weightItemSelected : ''}`}
                            onClick={() => handleWeightChange(weight, 'calloutText')}
                            style={{
                              fontWeight: weight
                            }}
                          >
                            {formatWeightDisplay(calloutTextFont.family, weight)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Body Text Section */}
              <div className={styles.fontSection} ref={bodyRef}>
                <h3 className={styles.fontSectionTitle}>Body Text</h3>
                <div className={styles.searchInputContainer}>
                  {!bodyFont.family && (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.searchIcon}>
                    <path d="M10.421 0C14.5954 0 18 3.40462 18 7.57899C18 11.7534 14.5954 15.158 10.421 15.158C8.67422 15.158 7.06155 14.5613 5.77761 13.5621L1.61719 17.7224C1.24712 18.0925 0.647591 18.0925 0.277544 17.7224C-0.0925028 17.3524 -0.0925265 16.7519 0.277544 16.3828L4.43796 12.2234C3.43876 10.9383 2.84203 9.32678 2.84203 7.58006C2.84203 3.40569 6.24668 0.00106589 10.4211 0.00106589L10.421 0ZM10.421 1.89475C7.27068 1.89475 4.73667 4.42874 4.73667 7.57899C4.73667 10.7292 7.27068 13.2632 10.421 13.2632C13.5712 13.2632 16.1052 10.7292 16.1052 7.57899C16.1052 4.42874 13.5712 1.89475 10.421 1.89475Z" fill="#D2D2D2"/>
                  </svg>
                  )}
                  <input
                    type="text"
                    placeholder="Search fonts"
                    value={bodyFont.family ? formatInputDisplay(bodyFont.family, bodyFont.weight) : bodySearch}
                    onChange={(e) => {
                      if (!bodyFont.family && !applySameFont) {
                        setBodySearch(e.target.value);
                        setShowBodyDropdown(true);
                      }
                    }}
                    onFocus={() => {
                      if (!bodyFont.family && !applySameFont) {
                        setShowBodyDropdown(true);
                      }
                    }}
                    className={`${styles.searchInput} ${bodyFont.family ? styles.searchInputSelected : ''}`}
                    style={{
                      fontFamily: bodyFont.family ? `"${bodyFont.family}", sans-serif` : 'inherit',
                      fontWeight: bodyFont.family ? bodyFont.weight : 'inherit'
                    }}
                    readOnly={!!bodyFont.family || applySameFont}
                    disabled={applySameFont && !bodyFont.family}
                  />
                  {bodyFont.family && !applySameFont && (
                    <button
                      className={styles.clearButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearFont('body');
                      }}
                      aria-label="Clear font selection"
                    >
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.5" y="0.5" width="21" height="21" rx="10.5" fill="#D0EAFE" fillOpacity="0.2"/>
                    <rect x="0.5" y="0.5" width="21" height="21" rx="10.5" stroke="#78B3F3"/>
                    <path d="M14.5 8.1125L13.8875 7.5L11 10.3875L8.1125 7.5L7.5 8.1125L10.3875 11L7.5 13.8875L8.1125 14.5L11 11.6125L13.8875 14.5L14.5 13.8875L11.6125 11L14.5 8.1125Z" fill="#505050"/>
                    </svg>
                    </button>
                  )}
                  {showBodyDropdown && !bodyFont.family && !applySameFont && !fontsLoading && fonts.length > 0 && (
                    <div className={styles.dropdown}>
                      {filterFonts(bodySearch, fonts).map((font) => (
                        <div
                          key={font.family}
                          className={styles.dropdownItem}
                          onClick={() => handleFontSelect(font, 'body')}
                        >
                          {font.family}
                        </div>
                      ))}
                      {filterFonts(bodySearch, fonts).length === 0 && bodySearch.trim() && (
                        <div className={styles.dropdownItem}>No fonts found</div>
                      )}
                    </div>
                  )}
                </div>
                {bodyFont.family && (
                  <div className={styles.fontWeightList}>
                    <div 
                      className={styles.fontNameRow}
                      onClick={() => !applySameFont && setShowBodyWeights(!showBodyWeights)}
                      style={{ cursor: applySameFont ? 'default' : 'pointer' }}
                    >
                      <span className={styles.chevronIcon} style={{ transform: showBodyWeights ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', opacity: applySameFont ? 0.5 : 1 }}>›</span>
                      <span className={styles.fontNameText}>{bodyFont.family}</span>
                    </div>
                    {showBodyWeights && !applySameFont && (
                      <div className={styles.weightList}>
                        {getAvailableWeights(bodyFont.family).map(weight => (
                          <div
                            key={weight}
                            className={`${styles.weightItem} ${bodyFont.weight === weight ? styles.weightItemSelected : ''}`}
                            onClick={() => handleWeightChange(weight, 'body')}
                            style={{
                              fontWeight: weight
                            }}
                          >
                            {formatWeightDisplay(bodyFont.family, weight)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
        </div>
      </div>
    </div>
  );
};

export default FontsPanel;

